/**
 * ClientSelect Component
 * A searchable dropdown for selecting clients
 * Sorted alphabetically with search functionality
 */

import { useState, useMemo, useCallback } from "react";
import { Check, ChevronsUpDown, Search, X } from "lucide-react";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "./ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";

// Remove diacritics for search comparison
const removeDiacritics = (str) => {
  if (!str) return "";
  return str
    .toLowerCase()
    .replace(/[ăâ]/g, 'a')
    .replace(/[î]/g, 'i')
    .replace(/[ș]/g, 's')
    .replace(/[ț]/g, 't')
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

// Get client display name
const getClientDisplayName = (client) => {
  if (!client) return "";
  return client.nume || client.denumire || 
         `${client.prenume || ""} ${client.nume_familie || ""}`.trim() || 
         "Client fără nume";
};

export function ClientSelect({
  clients = [],
  value,
  onValueChange,
  placeholder = "Selectează client...",
  emptyText = "Toți clienții",
  allowNone = true,
  allowCustom = false,
  customDisplayName = "",
  disabled = false,
  className,
  "data-testid": testId = "client-select",
}) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Sort clients alphabetically
  const sortedClients = useMemo(() => {
    return [...clients].sort((a, b) => {
      const nameA = removeDiacritics(getClientDisplayName(a));
      const nameB = removeDiacritics(getClientDisplayName(b));
      return nameA.localeCompare(nameB);
    });
  }, [clients]);

  // Filter clients by search query
  const filteredClients = useMemo(() => {
    if (!searchQuery.trim()) return sortedClients;
    
    const searchNormalized = removeDiacritics(searchQuery.trim());
    
    return sortedClients.filter(c => {
      const name = removeDiacritics(getClientDisplayName(c));
      return name.includes(searchNormalized);
    });
  }, [sortedClients, searchQuery]);

  // Get display text for selected value
  const selectedClient = useMemo(() => {
    if (!value || value === "none") return null;
    return clients.find(c => c.id === value);
  }, [clients, value]);

  const selectedDisplayText = useMemo(() => {
    if (!selectedClient) return emptyText;
    return getClientDisplayName(selectedClient);
  }, [selectedClient, emptyText]);

  const handleSelect = useCallback((clientId) => {
    onValueChange(clientId === "none" ? "" : clientId);
    setOpen(false);
    setSearchQuery("");
  }, [onValueChange]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn("w-full justify-between font-normal", className)}
          data-testid={testId}
        >
          <span className="truncate text-left flex-1">
            {selectedClient ? (
              <span className="text-green-600 font-medium">{selectedDisplayText}</span>
            ) : customDisplayName ? (
              <span className="text-foreground font-medium">{customDisplayName}</span>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[350px] p-0" align="start" data-client-select-popover>
        <Command shouldFilter={false}>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              placeholder="Caută după nume..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
              data-testid={`${testId}-search`}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="ml-2 rounded-sm opacity-70 hover:opacity-100"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <CommandList>
            <CommandEmpty>
              {searchQuery && allowCustom ? null : (
                searchQuery ? `Niciun client găsit pentru "${searchQuery}"` : "Nu există clienți"
              )}
            </CommandEmpty>
            <CommandGroup>
              {allowNone && (
                <CommandItem
                  value="none"
                  onSelect={() => handleSelect("none")}
                  className="cursor-pointer [&[data-selected=true]]:bg-slate-100 [&[data-selected=true]]:text-slate-900"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      (!value || value === "none") ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span className="text-muted-foreground">{emptyText}</span>
                </CommandItem>
              )}
              {allowCustom && searchQuery.trim() && (
                <CommandItem
                  value="__custom__"
                  onSelect={() => {
                    onValueChange("__custom__", searchQuery.trim());
                    setOpen(false);
                    setSearchQuery("");
                  }}
                  className="cursor-pointer [&[data-selected=true]]:bg-slate-100 [&[data-selected=true]]:text-slate-900"
                >
                  <Check className="mr-2 h-4 w-4 opacity-0" />
                  <span className="text-blue-600">Adaugă: <strong>{searchQuery.trim()}</strong></span>
                </CommandItem>
              )}
              {filteredClients.map((client) => {
                const displayName = getClientDisplayName(client);
                return (
                  <CommandItem
                    key={client.id}
                    value={client.id}
                    onSelect={() => handleSelect(client.id)}
                    className="cursor-pointer [&[data-selected=true]]:bg-slate-100 [&[data-selected=true]]:text-slate-900"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4 shrink-0",
                        value === client.id ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <span className="text-green-600">{displayName}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default ClientSelect;
